/**
 * Billing types for Stripe Checkout / Billing Portal integration.
 * The session-creation endpoint lives in AMS (task 072).
 */

/**
 * Request body for POST /api/billing/session.
 * `mode` determines whether we open a Stripe Checkout (new subscription)
 * or the Billing Portal (manage existing subscription).
 * `returnUrl` is where Stripe redirects after the hosted page.
 * `planSlug` selects the checkout target plan (task 233 tiered checkout);
 * innovate-service forwards it to AMS, which validates it against the
 * availability contract. Omitted for portal mode.
 * `flow` deep links into a Billing Portal flow (task 941): 'plan' opens Stripe's
 * subscription update flow, 'payment_method' the payment-method update flow.
 * Omitted lands on the portal home, which is where billing details are edited.
 */
export interface BillingSessionRequest {
  mode: 'checkout' | 'portal';
  returnUrl: string;
  planSlug?: string;
  flow?: 'plan' | 'payment_method';
}

/**
 * Response from POST /api/billing/session.
 * Returned by innovate-service in its standard wrapper shape:
 *   { success: boolean, data: { url: string } }
 * `url` is the Stripe-hosted page URL the browser should navigate to.
 */
export interface BillingSessionResponse {
  success: boolean;
  data: {
    url: string;
  };
}

/**
 * One feature entry in the plan catalog, carrying the display name so the UI
 * never renders raw feature keys.
 */
export interface PlanCatalogFeature {
  key: string;
  name: string;
  category: string;
}

/**
 * One plan in the customer-facing catalog served by GET /api/billing/plans
 * (innovate-service proxy → AMS /api/plans/catalog, task 231). Plans are
 * pre-filtered by AMS's availability contract (public, active, not deleted)
 * and ordered by displayOrder. `selfServe` means the plan is Stripe-provisioned
 * and purchasable in-app; false renders as contact-sales.
 */
export interface PlanCatalogPlan {
  slug: string;
  name: string;
  displayOrder: number;
  basePriceCents: number;
  selfServe: boolean;
  limits: Record<string, number>;
  features: PlanCatalogFeature[];
}

/**
 * Response from GET /api/billing/plans in the standard wrapper shape:
 *   { success: boolean, data: { plans: PlanCatalogPlan[] } }
 */
export interface PlanCatalogResponse {
  success: boolean;
  data: {
    plans: PlanCatalogPlan[];
  };
}

/**
 * Parsed payload from a 402 limit_exceeded error response.
 * Returned by innovate-service when an account-level cap is hit.
 */
export interface LimitExceededError {
  error: 'limit_exceeded';
  limit: string;
  current: number;
  cap: number;
}

/**
 * Parsed payload from a 403 feature_not_entitled error response.
 * Returned by innovate-service when accessing a gated feature.
 */
export interface FeatureNotEntitledError {
  error: 'feature_not_entitled';
  feature: string;
}

/**
 * Parsed payload from a 402 no_active_plan error response (task 464/465).
 * Returned by innovate-service when the account has NO active plan at all —
 * distinct from limit_exceeded (a plan exists but a cap was hit). The fix is
 * choosing a plan on the Billing page, not upgrading.
 */
export interface NoActivePlanError {
  error: 'no_active_plan';
  /** The AMS account the enforcement resolved against, when known. */
  accountId?: string;
}

/**
 * Parsed payload from a 402 spending_limit_reached error response (task 1159;
 * service task 1156). Returned when the billed account set a monthly spending
 * limit with the hard stop on and this month's billed usage cost has reached
 * it. The remedy is on Settings → Billing → Spending, not a plan change.
 */
export interface SpendingLimitReachedError {
  error: 'spending_limit_reached';
  limitCents: number;
  billedCents: number;
  /** The calendar month the counter is keyed by, e.g. `2026-10`. */
  period: string;
  message?: string;
}

/** Union of the entitlement enforcement error shapes. */
export type EntitlementError =
  | LimitExceededError
  | FeatureNotEntitledError
  | NoActivePlanError
  | SpendingLimitReachedError;

/** Narrow a raw API error response to LimitExceededError. */
export function isLimitExceededError(data: unknown): data is LimitExceededError {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as Record<string, unknown>)['error'] === 'limit_exceeded'
  );
}

/** Narrow a raw API error response to FeatureNotEntitledError. */
export function isFeatureNotEntitledError(data: unknown): data is FeatureNotEntitledError {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as Record<string, unknown>)['error'] === 'feature_not_entitled'
  );
}

/** Narrow a raw API error response to NoActivePlanError. */
export function isNoActivePlanError(data: unknown): data is NoActivePlanError {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as Record<string, unknown>)['error'] === 'no_active_plan'
  );
}

/** Narrow a raw API error response to SpendingLimitReachedError. */
export function isSpendingLimitReachedError(data: unknown): data is SpendingLimitReachedError {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as Record<string, unknown>)['error'] === 'spending_limit_reached'
  );
}

/**
 * Extract an EntitlementError from a raw axios-style error object, if present.
 * Returns null for any other error (generic 4xx, network error, etc.).
 */
export function extractEntitlementError(error: unknown): EntitlementError | null {
  if (typeof error !== 'object' || error === null) return null;

  const e = error as Record<string, unknown>;
  const status = e['status'] as number | undefined;
  const responseData = e['response'];

  if (status === 402 && isLimitExceededError(responseData)) {
    return responseData;
  }
  if (status === 402 && isNoActivePlanError(responseData)) {
    return responseData;
  }
  if (status === 402 && isSpendingLimitReachedError(responseData)) {
    return responseData;
  }
  if (status === 403 && isFeatureNotEntitledError(responseData)) {
    return responseData;
  }

  return null;
}

/**
 * The plan Stripe actually bills, read live from the Stripe subscription
 * (task 942). Stripe is the billing truth: wherever the app names the plan and
 * price a customer is *charged*, it renders this — not the AMS entitlement
 * plan, which may deliberately differ.
 */
export interface BilledPlanSummary {
  /** AMS plan slug the billed Stripe product maps to (null if unmapped). */
  slug: string | null;
  /** AMS plan display name for that slug (null if unmapped). */
  name: string | null;
  priceId: string | null;
  amountCents: number | null;
  currency: string | null;
  /** Stripe recurring interval — 'month' / 'year'. */
  interval: string | null;
  /** ISO date this amount is next charged (Stripe current_period_end). */
  nextBillingDate: string | null;
}

/**
 * The plan AMS grants (task 942). `isOverride` means AMS is deliberately
 * granting a plan other than the one Stripe bills — a comp, an internal
 * account, a test — and the surfaces mark it rather than implying it is billed.
 * `overrideReason` is operator-facing context and is NOT rendered to customers.
 */
export interface EntitlementPlanSummary {
  slug: string;
  name: string;
  isOverride: boolean;
  overrideReason: string | null;
  overrideAt: string | null;
}

/**
 * Read-only projection of the Stripe subscription served by the AMS
 * subscription-summary endpoint (task 627), carrying both plan faces since
 * task 942. Null-safe: accounts with no Stripe customer / no plan (and any
 * environment where the endpoint is not deployed) simply omit these lines.
 */
export interface SubscriptionSummary {
  /** The ENTITLEMENT plan slug — retained from task 627, unchanged in meaning. */
  planSlug: string | null;
  status: string | null;
  /**
   * ISO date the current Stripe billing period began (`current_period_start`,
   * task 963). With `currentPeriodEnd` this is the period the customer is
   * actually billed for — an anniversary window (Sep 8 – Oct 8), not the
   * calendar month the usage counters are keyed by.
   */
  currentPeriodStart: string | null;
  /** ISO date of the next renewal (Stripe `current_period_end`). */
  currentPeriodEnd: string | null;
  paymentMethod: {
    brand: string;
    last4: string;
    expMonth?: number;
    expYear?: number;
  } | null;
  billedPlan: BilledPlanSummary | null;
  entitlementPlan: EntitlementPlanSummary | null;
  /**
   * This calendar month's model spend split by who paid the provider (task
   * 1147; AMS task 1148). Integer micro-USD as STRINGS (AMS serializes its
   * BigInt counters that way), at catalog list prices before any markup:
   *   - `platformKeyCost`: calls on the platform's keys, billed on the Innovate
   *     invoice at the plan's rate.
   *   - `accountKeyCost`: calls on the account's own keys, paid to the provider,
   *     shown for information only.
   * AMS sends it with or without a subscription, "0" when there is no usage
   * row. Optional here only for AMS builds predating task 1148.
   */
  usage?: {
    period: string;
    accountKeyCost: string;
    platformKeyCost: string;
  } | null;
}

/**
 * Response from POST /api/billing/plan (task 965) — the in-app change of the
 * plan Stripe bills, in the standard wrapper shape:
 *   { success: boolean, data: { billedPlan, entitlementPlan } }
 *
 * The same two faces the subscription summary carries, so the page can say what
 * Stripe now charges and, on an overridden account, that the entitlements it
 * grants are unchanged. The AMS plan pointer follows by webhook, which is why
 * the page re-reads the summary after this answers rather than trusting it for
 * the entitlement side.
 */
export interface BillingPlanChangeResponse {
  success: boolean;
  data: {
    billedPlan: BilledPlanSummary | null;
    entitlementPlan: EntitlementPlanSummary | null;
  };
}

// ─── Spending limit (task 1159; service task 1156) ──────────────────────────

/** One threshold alert sent this month: the Spending page's "80% alert sent on Oct 19". */
export interface AccountSpendingAlert {
  thresholdPct: number;
  sentAt: string;
}

/**
 * The `spending` block on the member-reachable usage summary
 * (GET /api/accounts/usage). Every spending surface reads this one snapshot:
 * the Spending page, the Billing usage row, the Dashboard tile, the top-bar
 * indicator and the banners.
 */
export interface AccountSpending {
  /** The monthly limit in billed cents; null = no limit. */
  limitCents: number | null;
  /** Stop external calls once the limit is reached. */
  hardStop: boolean;
  /**
   * This month's usage cost in billed cents: raw counter × plan markup. Null
   * only when the plan (and so its markup) cannot be resolved.
   */
  billedCents: number | null;
  /** The configured thresholds, or the defaults [50, 80, 100] when unset. */
  alertThresholdsPct: number[];
  /** This month's alerts that have been sent, ascending by threshold. */
  alertsSent: AccountSpendingAlert[];
  /** `hardStop && billedCents >= limitCents`: external calls are being refused. */
  stopped: boolean;
}

/**
 * Body of PATCH /api/accounts/spending (owners and admins). All three keys are
 * required; the service validates thresholds as 1–5 distinct ascending whole
 * percentages between 1 and 100 (a `null` there means the default; the app
 * always sends the list).
 */
export interface UpdateAccountSpendingInput {
  limitCents: number | null;
  alertThresholdsPct: number[];
  hardStop: boolean;
}

/** What PATCH /api/accounts/spending returns: the saved settings, default thresholds applied. */
export interface AccountSpendingSettings {
  limitCents: number | null;
  alertThresholdsPct: number[];
  hardStop: boolean;
}
